import { Add, Delete } from "@mui/icons-material";
import { Alert, Button, FormControl, FormControlLabel, FormLabel, Grid, IconButton, List, ListItem, ListItemButton, ListItemText, Snackbar, Stack, Switch, TextField, Typography, styled } from "@mui/material";
import PropTypes from 'prop-types';
import { useContext, useState } from "react";
import { CreatePollContext } from "../context/CreatePollContextProvider";
import { DateTimePicker } from "@mui/x-date-pickers";
import dayjs from "dayjs";

const ImgStyled = styled('img')(({ theme }) => ({
    width: "100%",
    height: "100%",
    maxHeight: 400,
    marginRight: theme.spacing(6.25),
    borderRadius: theme.shape.borderRadius
}))


const CreatePollStep = ({ step, activeStep }) => {
    const { pollQuestion, setPollQuestion, pollOptions, setPollOptions, imgSrc, setImgSrc, setImgFile,
        groupsCanVote, setGroupsCanVote,
        hasVoteFee, setHasVoteFee,
        isValidVoteFee, setIsValidVoteFee,
        voteFee, setVoteFee,
        hasExpireDate, setHasExpireDate,
        isValidExpireDate, setIsValidExpireDate,
        expireDate, setExpireDate
    } = useContext(CreatePollContext)
    const [newOption, setNewOption] = useState('');
    const [alertState, setAlertState] = useState({ show: false, success: true, message: '' })

    if (step !== activeStep) {
        return null;
    }

    const handleCloseFormUpdateAlert = (/** @type {any} */ _event, /** @type {string} */ reason) => {
        if (reason === 'clickaway') {
            return
        }

        setAlertState({ show: false, success: true, message: '' })
    }

    const addNewOption = () => {
        const option = newOption.trim();
        setNewOption('');
        if (!option) {
            return;
        }
        setPollOptions([...pollOptions, newOption]);
    }

    const onChangeImage = (/** @type {{ target: { files: any; }; }} */ file) => {
        const reader = new FileReader()
        const { files } = file.target
        if (files && files.length !== 0) {
            const file = files[0];
            if (file?.size >= 10 * 1024 * 1024) {
                setAlertState({ show: true, success: false, message: 'The thumbnail is too big. Please select an image up to 10mb.' })
                return;
            }

            reader.onload = () => {
                // @ts-ignore
                setImgSrc(reader.result)
                setImgFile(files[0])
            }
            reader.readAsDataURL(files[0])
        }
    }

    const updateAmount = (/** @type {string} */ newAmount) => {
        if (!newAmount || newAmount.length === 0) {
            setIsValidVoteFee(false);
            setVoteFee(newAmount);
            return;
        }
        try {
            // @ts-ignore
            const mAmount = parseInt(newAmount.replaceAll(',', ''));
            setVoteFee(mAmount.toLocaleString('en-US'));
            if (mAmount > 0) {
                setIsValidVoteFee(true);
            } else {
                setIsValidVoteFee(false)
            }
        } catch (e) {
            console.warn(`invalid amount ${e.message}`);
        }
    }

    const updateExpireDate = (/** @type {Date} */ date) => {
        try {
            setExpireDate(date.toISOString());
            if(date > new Date()){
                setIsValidExpireDate(true);    
            } else {
                setIsValidExpireDate(false);
            }
        } catch (e) {
            setIsValidExpireDate(false);
        }
    }

    return <>
        <Grid item xs={12}>
            <FormControl fullWidth>
                <FormLabel sx={{ color: 'white' }} >Provide a question for this poll</FormLabel>
                <TextField value={pollQuestion} onChange={e => setPollQuestion(e.target.value)} />
            </FormControl>
        </Grid>
        <Grid item xs={12}>
            <FormControl fullWidth>
                <FormControlLabel control={<Switch defaultChecked={groupsCanVote} value={groupsCanVote} onChange={e => setGroupsCanVote(e.target.checked)} />} label="Allow group to vote" sx={{ color: 'white' }} />
            </FormControl>
        </Grid>
        <Grid item xs={12}>
            <FormControl fullWidth>
                <FormControlLabel control={<Switch defaultChecked={hasVoteFee} value={hasVoteFee} onChange={e => setHasVoteFee(e.target.checked)} />} label="Pay to vote" sx={{ color: 'white' }} />
            </FormControl>
        </Grid>
        {hasVoteFee && <Grid item xs={12}>
            <FormControl fullWidth error={!isValidVoteFee}>
                <FormLabel sx={{ color: 'white' }} >Millix amount</FormLabel>
                <TextField value={voteFee} onChange={e => updateAmount(e.target.value)} error={!isValidVoteFee} />
            </FormControl>
        </Grid>}
        <Grid item xs={12}>
            <FormControl fullWidth>
                <FormControlLabel control={<Switch defaultChecked={hasExpireDate} value={hasExpireDate} onChange={e => {
                    setIsValidExpireDate(false);
                    setHasExpireDate(e.target.checked)
                }} />} label="Automatically close" sx={{ color: 'white' }} />
            </FormControl>
        </Grid>
        {hasExpireDate && <Grid item xs={12}>
            <FormControl fullWidth error={!isValidExpireDate}>
                <FormLabel sx={{ color: 'white' }} >Close time</FormLabel>
                <DateTimePicker defaultValue={isValidExpireDate ? dayjs(expireDate) : undefined} onChange={e => updateExpireDate(// @ts-ignore 
                    e.$d)} />
            </FormControl>
        </Grid>}
        <Grid item xs={12}>
            <FormLabel sx={{ color: 'white' }} >Add an option</FormLabel>
            <Stack direction={'row'}>
                <FormControl fullWidth>
                    <TextField value={newOption} onChange={e => setNewOption(e.target.value)} onKeyDown={e => {
                        if (e.key === 'Enter') {
                            addNewOption();
                        }
                    }} />
                </FormControl>
                <Button color="primary" variant={'outlined'} sx={{ p: '10px' }} aria-label="add option" disabled={!newOption} onClick={addNewOption}>
                    <Add />
                </Button>
            </Stack>
        </Grid>
        {pollOptions.length < 2 && <Grid item xs={12}>
            <Alert variant='outlined' severity={'warning'}>
                You must add at least 2 options.
            </Alert>
        </Grid>}
        {pollOptions.length > 0 && <Grid item xs={12}>
            <List sx={{ borderColor: '#90caf9', borderWidth: '2px', borderStyle: 'solid', borderRadius: '4px' }}>
                {pollOptions.map((value, idx) => {
                    return (
                        <ListItem
                            key={value}
                            secondaryAction={
                                <IconButton edge="end" aria-label="delete" onClick={() => setPollOptions(pollOptions.filter((_, i) => i != idx))}>
                                    <Delete />
                                </IconButton>
                            }
                            disablePadding
                        >
                            <ListItemButton role={undefined}>
                                <ListItemText primary={value} sx={{ color: 'white' }} />
                            </ListItemButton>
                        </ListItem>
                    );
                })}
            </List>
        </Grid>}
        {/* <Grid item xs={12}>
            <Stack spacing={2} direction={'row'}>
                <Button component='label' variant='contained' htmlFor='poll-thumbnail-upload-image'>
                    Image
                    <input
                        hidden
                        type='file'
                        onChange={onChangeImage}
                        accept='image/png, image/jpeg'
                        id='poll-thumbnail-upload-image'
                    />
                </Button>
                <Typography variant='body2' sx={{ marginTop: 5, color: 'white' }}>
                    Select a thumbnail image for your poll
                </Typography>
            </Stack>
        </Grid>
        <Grid item xs={12}>
            <Grid item xs={4}>
                {imgSrc && <ImgStyled src={imgSrc} />}
            </Grid>
        </Grid> */}
        {alertState.show && (
            <Snackbar
                open={alertState.show}
                autoHideDuration={4000}
                onClose={handleCloseFormUpdateAlert}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
            >
                <Alert
                    onClose={handleCloseFormUpdateAlert}
                    severity={alertState.success ? 'success' : 'error'}
                    variant={'filled'}
                    elevation={6}
                    sx={{ width: '100%' }}
                >
                    {alertState.message}
                </Alert>
            </Snackbar>
        )}
    </>;
}

CreatePollStep.propTypes = {
    step: PropTypes.number,
    activeStep: PropTypes.number
}
export default CreatePollStep;